/**
 * Purchase-order detail — document-style view + status-gated actions.
 *
 * Renders the printable order (buyer letterhead pattern → supplier block,
 * dates, line table, totals) plus a sticky action rail whose buttons are
 * gated by the order's status:
 *   - draft     → Edit · Send · Delete draft
 *   - sent      → Mark confirmed · Cancel
 *   - confirmed → Close · Cancel
 *   - terminal (received / closed / cancelled) → no actions
 *
 * `send` numbers the order; `transitionStatus` drives the rest;
 * `deleteDraft` removes an unsent draft (ConfirmDialog-gated, since it's
 * destructive). Append-only elsewhere — cancel/close are status moves, not
 * deletes.
 *
 * @see prototype/purchases/purchase-order-detail.html — V1.1 baseline
 * @see packages/api-contract/src/procedures/purchase-orders.ts
 */
import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Ban,
  Check,
  FileCheck2,
  Mail,
  MessageCircle,
  PackageCheck,
  Pencil,
  Printer,
  Send,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import {
  Alert,
  Button,
  ConfirmDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  StatusPill,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate, paiseToInr, paiseToInrExact } from "../../../lib/format.js";
import { openDocPdf } from "../../../lib/pdf.js";
import { poStatusLabel, poStatusTone, type PoStatus } from "../lib/status.js";
import { PoVoidDialog } from "./po-void-dialog.js";

interface PoDetailPageProps {
  poId: string;
}

export function PoDetailPage({ poId }: PoDetailPageProps) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [voidOpen, setVoidOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const query = trpc.purchaseOrders.get.useQuery({ id: poId });

  const invalidate = async () => {
    await Promise.all([
      utils.purchaseOrders.get.invalidate({ id: poId }),
      utils.purchaseOrders.list.invalidate(),
    ]);
  };

  const send = trpc.purchaseOrders.send.useMutation({
    onSuccess: async (res: {
      outcome: "sent" | "awaiting_approval";
      poNumber: string | null;
      send: { channel: "email" | "whatsapp"; notificationId: string; deduplicated: boolean } | null;
    }) => {
      await invalidate();
      setSendOpen(false);
      if (res.outcome === "awaiting_approval") {
        // A configured approval chain (ADR-0031) gated this send — no number
        // was burned. The PO stays a draft until the chain clears.
        toast.success("Sent for approval", {
          description: "This order needs sign-off before it goes to the vendor.",
        });
        return;
      }
      const channelLabel =
        res.send?.channel === "whatsapp"
          ? "via WhatsApp"
          : res.send?.channel === "email"
            ? "via email"
            : "as sent";
      toast.success(`Purchase order ${res.poNumber} marked sent`, {
        description: res.send
          ? `The order is numbered and queued ${channelLabel}.`
          : "The order is numbered. No vendor notification was queued.",
      });
    },
    onError: (err: { message: string }) =>
      toast.error("Couldn't send order", { description: err.message }),
  });

  const transition = trpc.purchaseOrders.transitionStatus.useMutation({
    onSuccess: async (_res: unknown, vars: { status: string }) => {
      await invalidate();
      toast.success(`Order ${labelForStatus(vars.status)}`, {
        description: "The order's status has been updated.",
      });
    },
    onError: (err: { message: string }) =>
      toast.error("Couldn't update order", { description: err.message }),
  });

  const voidPo = trpc.purchaseOrders.void.useMutation({
    onSuccess: async () => {
      await invalidate();
      setVoidOpen(false);
      toast.success("Purchase order voided", {
        description: "The order is cancelled. The row stays as a historical record.",
      });
    },
    onError: (err: { message: string }) =>
      toast.error("Couldn't void order", { description: err.message }),
  });

  const deleteDraft = trpc.purchaseOrders.deleteDraft.useMutation({
    onSuccess: async () => {
      await utils.purchaseOrders.list.invalidate();
      setDeleteOpen(false);
      toast.success("Draft deleted", {
        description: "The draft order has been removed. Drafts never reach the ledger.",
      });
      void navigate({ to: "/procurement/purchase-orders" });
    },
    onError: (err: { message: string }) =>
      toast.error("Couldn't delete draft", { description: err.message }),
  });

  const busy =
    send.isPending || transition.isPending || deleteDraft.isPending || voidPo.isPending;

  if (query.isLoading) {
    return <p className="p-6 text-sm text-muted">Loading purchase order…</p>;
  }
  if (query.error || !query.data) {
    return (
      <div className="p-6">
        <Alert
          intent="bad"
          title="Couldn't load purchase order"
          description={query.error?.message ?? "Not found."}
        />
      </div>
    );
  }

  const po = query.data;
  const status = po.status as PoStatus;
  const vendorLocation = [po.vendorCity, po.vendorState].filter(Boolean).join(", ");

  return (
    <div className="flex min-h-full flex-col bg-bg">
      {/* Head */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
        <Link
          to="/procurement/purchase-orders"
          aria-label="Back to purchase orders"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
            Purchase order
            <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
              {po.poNumber ?? "Draft"}
            </span>
            <StatusPill tone={poStatusTone(status)} size="sm" dot>
              {poStatusLabel(status)}
            </StatusPill>
          </h1>
          <p className="mt-0.5 text-xs text-muted">
            <strong className="text-ink-2">{po.vendorName}</strong> · Order date{" "}
            {formatDate(po.orderDate)}
            {po.expectedDate ? ` · expected ${formatDate(po.expectedDate)}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => openDocPdf(`/api/purchase-orders/${poId}/pdf`)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-line bg-paper px-3.5 py-2 text-xs font-semibold text-ink transition hover:bg-bg"
        >
          <Printer className="h-3.5 w-3.5" />
          Print
        </button>
      </header>

      <main className="mx-auto grid w-full max-w-[1200px] flex-1 gap-5 px-6 pb-16 pt-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* Document */}
        <article className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
          <div className="border-b border-line bg-raised px-5 py-2.5 text-xs text-muted">
            <strong className="text-ink">{po.poNumber ?? "Draft order"}</strong> · {po.vendorName} ·{" "}
            {formatDate(po.orderDate)}
          </div>
          <div className="p-6">
            {/* Letterhead marker */}
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted">
                  Purchase order
                </p>
                <h2 className="mt-1 font-mono text-xl font-bold text-ink">
                  {po.poNumber ?? "Draft"}
                </h2>
              </div>
              <dl className="text-right text-sm">
                <div className="flex justify-end gap-2">
                  <dt className="text-muted">Order date</dt>
                  <dd className="font-mono text-ink-2">{formatDate(po.orderDate)}</dd>
                </div>
                {po.expectedDate ? (
                  <div className="mt-1 flex justify-end gap-2">
                    <dt className="text-muted">Delivery by</dt>
                    <dd className="font-mono text-ink-2">{formatDate(po.expectedDate)}</dd>
                  </div>
                ) : null}
              </dl>
            </div>

            {/* Supplier */}
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted">Supplier</p>
              <p className="mt-1 text-base font-bold text-ink">{po.vendorName}</p>
              {vendorLocation ? <p className="text-sm text-muted">{vendorLocation}</p> : null}
              {po.vendorGstin ? (
                <p className="mt-1 font-mono text-xs text-muted">GSTIN {po.vendorGstin}</p>
              ) : null}
            </div>

            {/* Line items */}
            <table className="mt-5 w-full border-collapse text-sm">
              <thead>
                <tr className="border-y border-line text-xs uppercase tracking-wide text-muted">
                  <th className="py-2 text-left font-medium">Item</th>
                  <th className="py-2 text-right font-medium">HSN</th>
                  <th className="py-2 text-right font-medium">Qty</th>
                  <th className="py-2 text-right font-medium">Rate (₹)</th>
                  <th className="py-2 text-right font-medium">Tax</th>
                  <th className="py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {po.lineItems.map((line) => (
                  <tr key={line.id} className="border-b border-line">
                    <td className="py-2.5 text-ink">{line.description}</td>
                    <td className="py-2.5 text-right font-mono text-muted">{line.hsn || "—"}</td>
                    <td className="py-2.5 text-right font-mono text-ink-2">
                      {formatMilli(line.qtyMilli)} {line.unit}
                    </td>
                    <td className="py-2.5 text-right font-mono text-ink-2">
                      {paiseToInrExact(line.ratePaise).replace("₹", "")}
                    </td>
                    <td className="py-2.5 text-right font-mono text-muted">
                      {formatBps(line.taxRateBps)}%
                    </td>
                    <td className="py-2.5 text-right font-mono font-semibold text-ink">
                      {paiseToInrExact(line.lineTotalPaise).replace("₹", "")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="mt-5 flex flex-col items-end gap-1.5 text-sm">
              <div className="flex w-full max-w-xs justify-between">
                <span className="text-muted">Subtotal · taxable value</span>
                <span className="font-mono text-ink-2">
                  {paiseToInrExact(po.subtotalPaise).replace("₹", "")}
                </span>
              </div>
              <div className="flex w-full max-w-xs justify-between">
                <span className="text-muted">GST</span>
                <span className="font-mono text-ink-2">
                  {paiseToInrExact(po.taxPaise).replace("₹", "")}
                </span>
              </div>
              <div className="flex w-full max-w-xs justify-between border-t border-line pt-1.5">
                <span className="font-semibold text-ink">PO total</span>
                <span className="font-mono text-base font-bold text-ink">
                  {paiseToInr(po.totalPaise)}
                </span>
              </div>
            </div>

            {po.vendorNote ? (
              <div className="mt-5 rounded-md border border-line bg-raised p-3 text-sm text-ink-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted">
                  Note to vendor
                </p>
                <p className="mt-1 whitespace-pre-line">{po.vendorNote}</p>
              </div>
            ) : null}
          </div>
        </article>

        {/* Action rail */}
        <aside className="w-full">
          <div className="sticky top-20 space-y-4">
            <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
              <h4 className="text-sm font-semibold text-ink">Actions</h4>
              <div className="mt-3 flex flex-col gap-2">
                {status === "draft" ? (
                  <>
                    <Button asChild intent="ghost" className="border border-line bg-paper justify-start">
                      <Link to="/procurement/purchase-orders/$poId/edit" params={{ poId }}>
                        <Pencil size={15} />
                        Edit
                      </Link>
                    </Button>
                    <Button
                      disabled={busy}
                      className="justify-start"
                      onClick={() => setSendOpen(true)}
                    >
                      <Send size={15} />
                      Send to vendor
                    </Button>
                    <Button
                      intent="ghost"
                      disabled={busy}
                      className="justify-start text-bad hover:bg-bad-50"
                      onClick={() => setDeleteOpen(true)}
                    >
                      <Trash2 size={15} />
                      Delete draft
                    </Button>
                  </>
                ) : null}

                {status === "sent" ? (
                  <>
                    <Button
                      disabled={busy}
                      className="justify-start"
                      onClick={() => transition.mutate({ id: poId, status: "confirmed" })}
                    >
                      <Check size={15} />
                      Mark confirmed
                    </Button>
                    <Button
                      intent="ghost"
                      disabled={busy}
                      className="justify-start text-bad hover:bg-bad-50"
                      onClick={() => setVoidOpen(true)}
                    >
                      <Ban size={15} />
                      Void order
                    </Button>
                  </>
                ) : null}

                {status === "confirmed" ? (
                  <>
                    {/* Jump straight into recording a GRN against this PO. The
                        composer route deep-links via ?po=<id> and prefills
                        vendor + one line per PO line (see #139). */}
                    <Button asChild disabled={busy} className="justify-start">
                      <Link to="/procurement/goods-receipts/new" search={{ po: poId }}>
                        <PackageCheck size={15} />
                        Receive goods
                      </Link>
                    </Button>
                    <Button
                      disabled={busy}
                      className="justify-start"
                      onClick={() => transition.mutate({ id: poId, status: "closed" })}
                    >
                      <Check size={15} />
                      Close order
                    </Button>
                    <Button
                      intent="ghost"
                      disabled={busy}
                      className="justify-start text-bad hover:bg-bad-50"
                      onClick={() => setVoidOpen(true)}
                    >
                      <Ban size={15} />
                      Void order
                    </Button>
                  </>
                ) : null}

                {status === "partially_received" ? (
                  <>
                    {/* Repeat-receipt entry point — the composer will prefill
                        the full ordered qty per line; user tweaks quantities
                        to match this receipt (remaining-balance default is a
                        follow-up, see #139 Part 2). */}
                    <Button asChild disabled={busy} className="justify-start">
                      <Link to="/procurement/goods-receipts/new" search={{ po: poId }}>
                        <PackageCheck size={15} />
                        Receive goods
                      </Link>
                    </Button>
                    <Button
                      disabled={busy}
                      className="justify-start"
                      onClick={() => transition.mutate({ id: poId, status: "closed" })}
                    >
                      <Check size={15} />
                      Short-close order
                    </Button>
                    <p className="text-xs leading-relaxed text-muted">
                      Goods have been received against this order, so it can't be voided — close it to
                      stop expecting the balance, or record the remaining receipt.
                    </p>
                  </>
                ) : null}

                {status === "received" || status === "closed" || status === "cancelled" ? (
                  <p className="text-sm text-muted">
                    This order is {poStatusLabel(status).toLowerCase()} — no further actions.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">Summary</h4>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted">Status</dt>
                  <dd>
                    <StatusPill tone={poStatusTone(status)} size="sm">
                      {poStatusLabel(status)}
                    </StatusPill>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Order total</dt>
                  <dd className="font-mono font-semibold text-ink">{paiseToInr(po.totalPaise)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Lines</dt>
                  <dd className="font-mono text-ink-2">{po.lineItems.length}</dd>
                </div>
              </dl>
            </div>
          </div>
        </aside>
      </main>

      <PoVoidDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        poNumber={po.poNumber}
        totalLabel={paiseToInr(po.totalPaise)}
        vendorName={po.vendorName}
        pending={voidPo.isPending}
        {...(voidPo.error ? { errorMessage: voidPo.error.message } : {})}
        onConfirm={({ reason }) => voidPo.mutate({ id: poId, ...(reason ? { reason } : {}) })}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this draft?"
        description={`The draft order to ${po.vendorName} (${paiseToInr(po.totalPaise)}) and its line items will be removed. Drafts never reach the ledger — there's nothing to reverse.`}
        intent="danger"
        confirmLabel={deleteDraft.isPending ? "Deleting…" : "Delete draft"}
        onConfirm={() => deleteDraft.mutate({ id: poId })}
      />

      <PoSendDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        vendorName={po.vendorName}
        vendorEmail={po.vendorEmail}
        vendorPhone={po.vendorPhone}
        vendorPhoneWhatsapp={po.vendorPhoneWhatsapp}
        whatsappBusinessEnabled={po.whatsappBusinessEnabled}
        pending={send.isPending}
        onConfirm={(sendMode) => send.mutate({ id: poId, sendMode })}
      />
    </div>
  );
}

type PoSendMode = "mark-sent" | "email" | "whatsapp";

interface PoSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorName: string;
  vendorEmail: string | null;
  vendorPhone: string | null;
  vendorPhoneWhatsapp: boolean;
  whatsappBusinessEnabled: boolean;
  pending: boolean;
  onConfirm: (sendMode: PoSendMode) => void;
}

function PoSendDialog({
  open,
  onOpenChange,
  vendorName,
  vendorEmail,
  vendorPhone,
  vendorPhoneWhatsapp,
  whatsappBusinessEnabled,
  pending,
  onConfirm,
}: PoSendDialogProps) {
  const emailReason = vendorEmail ? null : "Add a vendor email to use this channel.";
  const whatsappReason = !whatsappBusinessEnabled
    ? "WhatsApp Business add-on is not enabled for this workspace."
    : !vendorPhone
      ? "Add a vendor phone number to use WhatsApp."
      : !vendorPhoneWhatsapp
        ? "Mark the vendor phone as WhatsApp-enabled first."
        : null;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Send purchase order</DialogTitle>
          <DialogDescription>
            Choose how this order should leave Flint for {vendorName}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5">
          <SendChoiceButton
            icon={FileCheck2}
            title="Only mark as sent"
            description="Assign the PO number and update status without queuing a vendor message."
            disabled={pending}
            onClick={() => onConfirm("mark-sent")}
          />
          <SendChoiceButton
            icon={Mail}
            title="Send via email"
            description={vendorEmail ?? emailReason}
            disabled={pending || emailReason !== null}
            disabledReason={emailReason}
            onClick={() => onConfirm("email")}
          />
          <SendChoiceButton
            icon={MessageCircle}
            title="Send via WhatsApp"
            description={vendorPhone && vendorPhoneWhatsapp ? `+91 ${vendorPhone}` : whatsappReason}
            disabled={pending || whatsappReason !== null}
            disabledReason={whatsappReason}
            onClick={() => onConfirm("whatsapp")}
          />
        </div>

        <DialogFooter>
          <Button type="button" intent="ghost" disabled={pending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}

interface SendChoiceButtonProps {
  icon: LucideIcon;
  title: string;
  description: string | null;
  disabled: boolean;
  disabledReason?: string | null;
  onClick: () => void;
}

function SendChoiceButton({
  icon: Icon,
  title,
  description,
  disabled,
  disabledReason,
  onClick,
}: SendChoiceButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={disabledReason ?? undefined}
      onClick={onClick}
      className={[
        "flex w-full items-start gap-3 rounded-md border border-line bg-paper p-3 text-left transition",
        disabled ? "cursor-not-allowed opacity-55" : "hover:border-sky-300 hover:bg-sky-50",
      ].join(" ")}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-raised text-ink-2">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">{title}</span>
        {description ? (
          <span className="mt-0.5 block text-xs leading-relaxed text-muted">{description}</span>
        ) : null}
      </span>
    </button>
  );
}

function labelForStatus(status: string): string {
  if (status === "confirmed") return "confirmed";
  if (status === "closed") return "closed";
  if (status === "cancelled") return "cancelled";
  return "updated";
}

function formatMilli(milli: number): string {
  const whole = Math.trunc(milli / 1000);
  const remainder = milli % 1000;
  if (remainder === 0) return whole.toLocaleString("en-IN");
  return `${whole.toLocaleString("en-IN")}.${String(remainder).padStart(3, "0").replace(/0+$/, "")}`;
}

function formatBps(bps: number): string {
  const whole = Math.trunc(bps / 100);
  const remainder = bps % 100;
  if (remainder === 0) return String(whole);
  return `${whole}.${String(remainder).padStart(2, "0").replace(/0+$/, "")}`;
}
